import type {
  AdminUser,
  AdminUsersResponse,
  CreateAdminUserPayload,
  GetAdminUsersPayload,
  ImpersonateUserResponse,
  UpdateAdminUserPayload,
  UpdateAdminUserStatusPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AdminUserResponse = { user: AdminUser };
type AdminUserMutationResponse = { user: AdminUser; message: string };

const adminUserListTags = (result: AdminUsersResponse | undefined) =>
  result
    ? [
        ...result.users.map(({ id }) => ({ type: 'AdminUser' as const, id })),
        { type: 'AdminUser' as const, id: 'LIST' },
      ]
    : [{ type: 'AdminUser' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/admin` (`adminUserController`, prefix `/admin/users`) — admin
 * panel user management plus impersonation (same server module, same controller file).
 *
 * `impersonateUser` is a mutation with no `providesTags`/`invalidatesTags`: it doesn't change any
 * `AdminUser` data (the target user's row is untouched — the route only mints a scoped JWT), and
 * per the project plan the resulting impersonation *state* (the minted token, the "who am I
 * impersonating" flag) lives entirely client-side in sessionStorage, wired up in Phase 3 — not
 * something this cache layer should track. There is no server-side "stop impersonation" route:
 * `grep`ing `apps/server/src/modules` for "impersonat" only turns up this one `POST
 * /admin/users/:user_id/impersonate` endpoint — stopping is purely a client-side token swap back
 * to the admin's own stored tokens, nothing to call here.
 */
export const adminUserApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<AdminUsersResponse, GetAdminUsersPayload>({
      query: (payload) => ({ path: ['admin', 'users'], method: 'get', query: payload }),
      providesTags: (result) => adminUserListTags(result),
    }),

    getAdminUserById: builder.query<AdminUser, string>({
      query: (userId) => ({ path: ['admin', 'users', { param: 'user_id', value: userId }], method: 'get' }),
      transformResponse: (response: AdminUserResponse) => response.user,
      providesTags: (_result, _error, userId) => [{ type: 'AdminUser', id: userId }],
    }),

    createAdminUser: builder.mutation<AdminUser, CreateAdminUserPayload>({
      query: (body) => ({ path: ['admin', 'users'], method: 'post', body }),
      transformResponse: (response: AdminUserMutationResponse) => response.user,
      invalidatesTags: [{ type: 'AdminUser', id: 'LIST' }],
    }),

    updateAdminUser: builder.mutation<AdminUser, { userId: string; payload: UpdateAdminUserPayload }>({
      query: ({ userId, payload }) => ({
        path: ['admin', 'users', { param: 'user_id', value: userId }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AdminUserMutationResponse) => response.user,
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'AdminUser', id: userId },
        { type: 'AdminUser', id: 'LIST' },
      ],
    }),

    updateAdminUserStatus: builder.mutation<AdminUser, { userId: string; payload: UpdateAdminUserStatusPayload }>({
      query: ({ userId, payload }) => ({
        path: ['admin', 'users', { param: 'user_id', value: userId }, 'status'],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AdminUserMutationResponse) => response.user,
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'AdminUser', id: userId },
        { type: 'AdminUser', id: 'LIST' },
      ],
    }),

    impersonateUser: builder.mutation<ImpersonateUserResponse, string>({
      query: (userId) => ({
        path: ['admin', 'users', { param: 'user_id', value: userId }, 'impersonate'],
        method: 'post',
      }),
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useGetAdminUserByIdQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useUpdateAdminUserStatusMutation,
  useImpersonateUserMutation,
} = adminUserApi;
